<script setup lang="ts">
import { onBeforeMount, nextTick, ref, watch, computed } from 'vue';
import { cloneDeep, pickBy } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import type { ApplicantId, Applicant } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import { formatList, getFullName } from '@/helpers/format';
import api from '@/api';
import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useHouseholdApplicants } from '@/helpers/useHouseholdApplicants';
import { ApplicantType, BorrowerType, EmploymentType } from '@/helpers/const';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack } = useErhlCustomerLoanAppProcess();
const { flagSessionExpired } = useSessionExpiry();

const isReady = ref(false);
const isLoading = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);
const expenseTypesError = ref(false);

const isSecondaryDeclaration = false;
const isIncomeExpenseRequest = false;

const hasNonIndividualBorrower = computed(
  () =>
    loanApp.value.borrowerGroups?.some((g) => g.type === BorrowerType.COMPANY || g.type === BorrowerType.TRUST) ??
    false,
);

/** Names of the other applicants, whose expenses are requested from them via email */
const secondaryApplicantNames = computed(() =>
  formatList(
    loanApp.value.applicants
      .filter((a) => a.type === ApplicantType.SECONDARY && a.id !== authorisedUser.value?.id)
      .map(getFullName),
  ),
);

const showCommercialExpenseUI = computed(() => hasNonIndividualBorrower.value);

const getApplicantsFromGroup = (group: BorrowerGroup): ApplicantId[] =>
  group.applicants?.map((a) => a.applicantId as ApplicantId) ?? [];

/**
 * Get all applicants eligible for expense collection.
 * For Regulated scenarios: Collect from ALL directors/trustees/individuals (no skip).
 * For Unregulated scenarios: Expenses step is skipped entirely (handled by state machine).
 */
const eligibleExpenseApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialExpenseUI.value) return [];

  // For Regulated + End Debt: collect expenses from ALL applicants in all borrower groups
  // No skip logic - directors of trading companies are included
  const ids = (loanApp.value.borrowerGroups ?? []).flatMap(getApplicantsFromGroup);

  return [...new Set(ids)]; // Deduplicates applicant IDs
});

const isMarried = (applicant: Applicant): boolean =>
  applicant.relationshipStatus === 'married_or_defacto' &&
  applicant.relatedApplicantIndex !== undefined &&
  applicant.relatedApplicantIndex >= 0;

const getPartnerId = (applicant: Applicant): ApplicantId | undefined => {
  if (applicant.relatedApplicantIndex === undefined || applicant.relatedApplicantIndex < 0) return undefined;
  return loanApp.value.applicants[applicant.relatedApplicantIndex]?.id as ApplicantId | undefined;
};

const hasEligiblePartner = (applicant: Applicant, eligible: ApplicantId[]): boolean =>
  isMarried(applicant) && eligible.includes(getPartnerId(applicant) as ApplicantId);

/**
 * Apply household grouping to eligible applicants
 * Married couples among eligible applicants share one expense form
 */
const commercialHouseholdExpenseApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialExpenseUI.value) return [];

  const eligible = eligibleExpenseApplicants.value;
  if (eligible.length === 0) return [];

  const eligibleApplicants = loanApp.value.applicants.filter((a) => eligible.includes(a.id as ApplicantId));
  const hasMarriedPairs = eligibleApplicants.some((a) => hasEligiblePartner(a, eligible));

  if (!hasMarriedPairs) return eligible;

  // Group married couples using reduce - first in pair becomes household head
  return eligibleApplicants.reduce<{ heads: ApplicantId[]; processed: Set<ApplicantId> }>(
    (acc, applicant) => {
      const applicantId = applicant.id as ApplicantId;
      if (acc.processed.has(applicantId)) return acc;

      acc.processed.add(applicantId);
      acc.heads.push(applicantId);

      const partnerId = getPartnerId(applicant);
      if (hasEligiblePartner(applicant, eligible) && partnerId) {
        acc.processed.add(partnerId);
      }

      return acc;
    },
    { heads: [], processed: new Set() },
  ).heads;
});

const applicantCompletionStatuses = ref<{ id: ApplicantId; status: 'completed' | 'incomplete' }[]>([]);

const { applicantsMarried, householdValidApplicants } = useHouseholdApplicants(loanApp, authorisedUser, {
  isSecondaryDeclaration,
  isIncomeExpenseRequest,
});

/** Get the list of applicants to display based on flow type */
const displayApplicants = computed<ApplicantId[]>(() =>
  showCommercialExpenseUI.value ? commercialHouseholdExpenseApplicants.value : householdValidApplicants.value,
);

const addCompletionStatus = (applicantIds: ApplicantId[]) =>
  applicantIds.map((applicantId) => ({
    applicantId,
    completionStatus: applicantCompletionStatuses.value.find((s) => s.id === applicantId)?.status ?? 'incomplete',
  }));

const householdValidApplicantsExpenses = computed(() => addCompletionStatus(householdValidApplicants.value));
const commercialHouseholdExpenseApplicantsWithStatus = computed(() =>
  addCompletionStatus(commercialHouseholdExpenseApplicants.value),
);

const validApplicantIds = new Set(loanApp.value.applicants.map((a) => a.id));
const financials = ref(pickBy(cloneDeep(loanApp.value.financials), (_v, id) => validApplicantIds.has(id)));
const expenseRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();

const activeTab = ref<ApplicantId | undefined>(
  displayApplicants.value.length === 1 ? displayApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id]?.expenses?.numberOfDependants !== undefined;

/** Checks if the applicant's primary employment type is "Retired". */
const isApplicantRetired = (applicantId: ApplicantId): boolean => {
  const firstEmployment = financials.value[applicantId]?.income?.employment?.[0];
  return firstEmployment?.type === EmploymentType.RETIRED;
};

const findFirstInvalidIndex = async (refs: (typeof expenseRefs.value)[number][]): Promise<number> => {
  const results = await Promise.all(refs.map(async (ref) => (await ref?.update()) ?? false));
  return results.findIndex((isValid) => !isValid);
};

const validateExpenses = async (): Promise<boolean> => {
  if (isSecondaryDeclaration && applicantsMarried) return true;

  const applicantsToValidate = displayApplicants.value;
  const refs = expenseRefs.value.slice(0, applicantsToValidate.length);
  const invalidIndex = await findFirstInvalidIndex(refs);

  if (invalidIndex === -1) return true;

  activeTab.value = applicantsToValidate[invalidIndex];
  window.scrollTo(0, 0);
  return false;
};

const saveAndContinue = async () => {
  if (expenseTypesError.value || expenseTypes.value.length === 0) {
    expenseTypesError.value = true;
    return;
  }
  if (await validateExpenses()) {
    isLoading.value = true;
    const applicationDraftId = loanApp.value.applicationDraftId;
    try {
      saveAndGoNext({
        financials: financials.value,
        applicationDraftId,
      });
    } catch (error) {
      if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
        flagSessionExpired();
        return;
      }
      console.error('Failed to save draft:', error);
    } finally {
      isLoading.value = false;
    }
  }
};

const sortByOrder = (a: ExpenseTypeDto, b: ExpenseTypeDto) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

const loadExpenseTypes = async () => {
  isReady.value = false;
  expenseTypesError.value = false;
  try {
    const response = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    expenseTypes.value = response.expenseTypes?.sort(sortByOrder) ?? [];
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to load expense types:', error);
    expenseTypesError.value = true;
  } finally {
    isReady.value = true;
  }
};

onBeforeMount(loadExpenseTypes);

/** Update completion statuses when form state changes */
watch(
  () => [isReady.value, isLoading.value, activeTab.value],
  async () => {
    if (!isReady.value || isLoading.value) return;

    await nextTick();

    const applicants = displayApplicants.value;
    const refs = expenseRefs.value.slice(0, applicants.length);

    const statuses = await Promise.all(
      refs.map(async (ref, i) => ({
        id: applicants[i],
        status: ((await ref?.validate()) ?? false ? 'completed' : 'incomplete') as 'completed' | 'incomplete',
      })),
    );

    applicantCompletionStatuses.value = statuses;
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        <template v-if="hasNonIndividualBorrower">
          Please outline the borrower's
          <BxHighlight>expenses.</BxHighlight>
        </template>
        <template v-else>
          What are your household
          <BxHighlight>expenses</BxHighlight>?
        </template>
      </h2>
    </div>
    <div v-if="!showCommercialExpenseUI" class="my-4">
      <p v-if="loanApp.applicants.length > 1">
        Please enter your own expenses. We will request this information from {{ secondaryApplicantNames }} via email.
      </p>
    </div>

    <!-- Commercial Flow: Iterate over eligible applicants with household grouping -->
    <div v-if="showCommercialExpenseUI" class="my-6 grid gap-4">
      <div v-for="applicant in commercialHouseholdExpenseApplicantsWithStatus" :key="applicant.applicantId">
        <BxCard :class="applicant.applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block">
              <BxIcon name="customer" />
            </span>
            {{ getFullName(loanApplicants[applicant.applicantId]) }}
          </h2>
          <ExpensesForm
            :ref="expenseRefs.set"
            v-model="financials[applicant.applicantId].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="commercialHouseholdExpenseApplicants.length > 1"
            :is-retired="isApplicantRetired(applicant.applicantId)"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>

        <BxCard :class="applicant.applicantId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="flex items-center font-medium">
                <span class="mr-3 hidden md:block">
                  <BxIcon name="customer" />
                </span>
                <span>{{ getFullName(loanApplicants[applicant.applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400">({{ applicant.completionStatus }})</span>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                :id="applicant.applicantId + '-add-expenses'"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicant.applicantId"
                @click="activeTab = applicant.applicantId"
              >
                {{ isCompleted(applicant.applicantId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <!-- Original Flow: Use householdValidApplicants -->
    <div v-else class="my-6 grid gap-4">
      <div v-for="applicant in householdValidApplicantsExpenses" :key="applicant.applicantId">
        <BxCard :class="applicant.applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicant.applicantId]) }}
          </h2>
          <ExpensesForm
            v-if="isIncomeExpenseRequest && applicantsMarried"
            :ref="expenseRefs.set"
            v-model="financials[loanApp.applicants[0].id].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            :is-retired="isApplicantRetired(applicant.applicantId)"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
          <ExpensesForm
            v-else
            :ref="expenseRefs.set"
            v-model="financials[applicant.applicantId].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            :is-retired="isApplicantRetired(applicant.applicantId)"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>
        <BxCard :class="applicant.applicantId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="font-medium">
                <span>{{ getFullName(loanApplicants[applicant.applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400">({{ applicant.completionStatus }})</span>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                variant="secondary"
                :disabled="activeTab && activeTab !== applicant.applicantId"
                @click="activeTab = applicant.applicantId"
              >
                {{ isCompleted(applicant.applicantId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <BxErrorPanel v-if="expenseTypesError" class="mt-6">
      We couldn't load expense categories.
      <BxButton variant="link" @click="loadExpenseTypes()">Try again</BxButton>
    </BxErrorPanel>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" :loading="isLoading" @click="saveAndContinue()">
        {{ showCommercialExpenseUI ? 'Continue' : 'Save and continue' }}
      </BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
